import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import React, { CSSProperties, useEffect, useState } from 'react';
import Modal from 'react-modal';
import '../Modal.css';
import { ReactComponent as CloseLogo } from '../../../assets/ICONS/close.svg';

interface CustomModalProp {
  show: boolean;
  onClose?: { (): void };
  submitFunc?: any;
  buttonMsg?: string;
  headerMsg: string;
  isSubmitBtnDisabled?: boolean;
  style?: CSSProperties;
  requireConfirmation?: boolean;
  confirmationMsg?: string;
}

export const CustomModal: React.FC<CustomModalProp> = ({
  buttonMsg = 'Save',
  headerMsg,
  show,
  submitFunc,
  onClose,
  children,
  isSubmitBtnDisabled,
  style,
  requireConfirmation = false,
  confirmationMsg = 'I understand the consequences of this action',
}) => {
  const [modalClass, setModalClass] = useState<string>('fixModal');
  const [overlayClass, setOverlayClass] = useState<string>('customOverlay');
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  useEffect(() => {
    if (show) {
      setModalClass('fixModal');
      setOverlayClass('customOverlay');
      setIsConfirmed(false);
    } else {
      setModalClass('fixModal closeAnimation');
      setOverlayClass('customOverlay closeOverlay');
    }
  }, [show]);

  const handleClose = () => {
    // Don't close if submit button is disabled (operation in progress)

    setModalClass('fixModal closeAnimation');
    setOverlayClass('customOverlay closeOverlay');
    setTimeout(() => {
      if (onClose) onClose();
    }, 300);
  };

  const handleSubmit = () => {
    if (submitFunc) {
      // Only call submitFunc, don't close the modal
      // The parent component will handle closing via onClose when operation completes
      submitFunc();
    }
  };

  const isSubmitDisabled = requireConfirmation
    ? !isConfirmed || isSubmitBtnDisabled
    : isSubmitBtnDisabled;

  return (
    <Modal
      isOpen={show}
      onAfterOpen={() => {
        setModalClass('fixModal');
        setOverlayClass('customOverlay');
      }}
      onRequestClose={handleClose}
      shouldCloseOnOverlayClick={true} // Prevent closing on overlay click during operation
      bodyOpenClassName="no-scroll"
      ariaHideApp={false}
      style={{ content: style }}
      className={modalClass}
      overlayClassName={overlayClass}
    >
      <Grid container spacing={0}>
        <Grid item md={12} sm={12} xs={12} className="modalHeader">
          <div>
            <span className="modalTitle">{headerMsg}</span>
            <span className="modalClose" onClick={handleClose}>
              <CloseLogo />
            </span>
          </div>
        </Grid>
        <div
          style={{
            padding: '2rem',
            width: '100%',
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
        >
          {children}

          {requireConfirmation && (
            <div
              style={{
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid #e0e0e0',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isConfirmed}
                    onChange={(e) => setIsConfirmed(e.target.checked)}
                    color="primary"
                    disabled={isSubmitBtnDisabled}
                  />
                }
                label={
                  <span style={{ fontSize: '14px', color: '#333' }}>
                    {confirmationMsg}
                  </span>
                }
              />
            </div>
          )}
        </div>
        <div className="mt-0 modalFooter">
          {submitFunc && (
            <button
              type="button"
              className="btn btn-primary ml-10"
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              {buttonMsg}
            </button>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleClose}
            disabled={isSubmitBtnDisabled}
          >
            Cancel
          </button>
        </div>
      </Grid>
    </Modal>
  );
};
